import {
  BoundTextField,
  Button,
  ButtonMenu,
  Chip,
  Css,
  Filters,
  GridColumn,
  GridDataRow,
  GridRowKind,
  GridStyle,
  GridTable,
  GridTableApi,
  Icon,
  IconButton,
  MultiSelectField,
  Palette,
  PresentationProvider,
  TestIds,
  Tooltip,
  column,
  emptyCell,
  getTableStyles,
  marker,
  multiFilter,
  useComputed,
  useFilter,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { useCallback, useMemo } from "react";
import { SearchBox } from "src/components/SearchBox";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { DeleteConfirmationModal } from "src/routes/components/DeleteConfirmationModal";
import { TableActions } from "src/routes/layout/TableActions";
import { DuplicateVariantModal } from "src/routes/libraries/design-catalog/personalization-option/components/DuplicateVariantModal";
import {
  EditPersonalizationOptionWizard_OptionCombinationFragment,
  EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment,
} from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import {
  FormValue,
  MaterialDimensionOption,
  OnDuplicateProduct,
  PackageOptionRow,
  PersonalizationPlaceholderRow,
  PlaceholderColumn,
  buildPlaceholderColumns,
  collapseMaterialToLocation,
  collapseWouldDiscardProducts,
  expandMaterial,
  hasEmptyDuplicateTarget,
  hasProductForColumn,
  materialFilterOptions,
  materialLocationIds,
  removeDuplicateCombinations,
} from "src/routes/libraries/design-catalog/personalization-option/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/routes/libraries/product-catalog/components/product-images-viewer/ProductImageViewer";

type PackageVariantsGridTableProps = {
  formState: ObjectState<FormValue>;
  /** The controlled slots (form-state rows) in display order; the grid derives per-material columns from them. */
  slots: PersonalizationPlaceholderRow[];
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
  /** The design package's target groups; the combination dimensions among them (e.g. Scheme, Spec level) become row filters. */
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
  tid: TestIds;
  onOpenProductSearch: (placeholderTliId: string, packageOption: PackageOptionRow, column: PlaceholderColumn) => void;
};

type HeaderRow = { kind: "header"; id: string; data: undefined };
type PackageOptionParentRow = { kind: "packageOption"; id: string; data: PackageOptionRow };
type ProductsRow = { kind: "products"; id: string; data: PackageOptionRow };
type AddPackageOptionRow = { kind: "addPackageOption"; id: string; data: undefined };
type Row = HeaderRow | PackageOptionParentRow | ProductsRow | AddPackageOptionRow;

/**
 * Client-side row filter. Keys: `brand`, one `combinationFilterKey(rpogId)` per Scheme, Spec level combination
 * and one `materialDimensionFilterKey(dimName)` per material attribute dimension
 * present on the assigned products.
 */
type VariantsFilter = Record<string, string[] | undefined>;

/** Filter key for a combination dimension (target group / RPOG, e.g. Scheme, Spec level). */
function combinationFilterKey(targetGroupId: string): string {
  return `combination:${targetGroupId}`;
}

/** Filter key for a material attribute dimension, keyed by name so the same dimension across items merges (e.g. Finish, Width). */
function materialDimensionFilterKey(dimensionName: string): string {
  return `dimension:${dimensionName}`;
}

/** Each slot column fills available width but never shrinks below this; past it the table scrolls. */
const slotMinWidthPx = 286;
/** Fixed width of the sticky combinations/actions column. */
const actionsWidthPx = 500;

// Full GridStyle (not a def) so our headerCellCss override isn't dropped; header hugs content + keeps padding.
// `bordered: false` — we draw the table frame ourselves on row wrappers (below) because cell-level
// borders from `bordered: true` fight cell-level sticky columns on horizontal scroll.
const baseTableStyle = getTableStyles({ allWhite: true, bordered: false, rowHeight: "flexible", vAlign: "top" });
const tableStyle: GridStyle = {
  ...baseTableStyle,
  presentationSettings: { wrap: false },
  headerCellCss: { ...baseTableStyle.headerCellCss, ...Css.add("height", "auto").pyPx(10).$ },
  firstRowCss: Css.bt.bl.br.bcGray200.$,
  nonHeaderRowCss: Css.bl.br.bcGray200.$,
};

/**
 * The package-option variants matrix as a `GridTable`: package options are rows, controlled slots
 * are columns. The package name (sticky left) and combinations/actions (sticky right) stay pinned
 * while the slot columns scroll horizontally. Owns the Search / Filter toolbar.
 */
export const PackageVariantsGridTable = observer(function PackageVariantsGridTable(
  props: PackageVariantsGridTableProps,
) {
  const { formState, slots, targetCombinations, targetGroups, tid, onOpenProductSearch } = props;
  const { openModal } = useModal();
  const api = useGridTableApi<Row>();

  // Collapsing is lossy, so confirm first only when it would actually drop a differing product.
  const onExpandMaterial = useCallback(
    (materialVariantId: string) =>
      expandMaterial(formState, materialVariantId, materialLocationIds(formState.placeholders.rows, materialVariantId)),
    [formState],
  );
  const onCollapseMaterial = useCallback(
    (materialVariantId: string, winningLocationId: string) => {
      if (!collapseWouldDiscardProducts(formState.packageOptions.value, materialVariantId, winningLocationId)) {
        collapseMaterialToLocation(formState, materialVariantId, winningLocationId);
        return;
      }
      const slots = formState.placeholders.rows.filter((p) => p.data.value.materialVariant?.id === materialVariantId);
      const featureName = slots[0]?.data.value.name ?? "";
      openModal({
        content: (
          <ConfirmationModal
            title={`Group '${featureName}'`}
            label={`Group ${slots.length} slots`}
            confirmationMessage={
              <>
                <p css={Css.sm.gray900.mb2.$}>
                  Grouping all slots of the feature '{featureName}' will apply the customization of the main slot to all
                  other grouped slots. Once you unlink the grouping, all original slot customizations will NOT be
                  restored.
                </p>
                <p css={Css.smSb.gray900.mb1.$}>Grouped slots:</p>
                <ul css={Css.sm.gray900.pl3.$}>
                  {slots.map((p) => (
                    <li key={p.id.value}>{p.data.value.location.displayLocationPath}</li>
                  ))}
                </ul>
              </>
            }
            onConfirmAction={() => collapseMaterialToLocation(formState, materialVariantId, winningLocationId)}
          />
        ),
      });
    },
    [formState, openModal],
  );

  const onDuplicateProduct: OnDuplicateProduct = useCallback(
    (packageOption, { materialVariantId, placeholder, locationId }, productMaterial) =>
      openModal({
        content: (
          <DuplicateVariantModal
            formState={formState}
            materialVariantId={materialVariantId}
            featureName={placeholder.data.value.name}
            productMaterial={productMaterial}
            sourcePackageOption={packageOption}
            sourceLocationId={locationId}
          />
        ),
      }),
    [formState, openModal],
  );

  // The combination dimensions to offer as filters (e.g. Scheme, Spec level): the target groups whose
  // options actually appear in this option's combinations. Stable across renders.
  const combinationGroups = useMemo(() => {
    const comboOptionIds = new Set(targetCombinations.flatMap((c) => c.options.map((o) => o.id)));
    return targetGroups.filter((g) => g.options.some((o) => comboOptionIds.has(o.id)));
  }, [targetGroups, targetCombinations]);

  // Brand + material-dimension filter options, derived from the products currently assigned (reactive).
  const productAttributeOptions = useComputed(() => {
    const materials = formState.packageOptions.value
      .flatMap((po) => po.products)
      .map((p) => p.productMaterial?.data)
      .compact();
    return materialFilterOptions(materials);
  }, [formState]);

  const filterDefs = useMemo(
    () => ({
      // Combination dimensions (e.g. Scheme, Spec level)...
      ...Object.fromEntries(
        combinationGroups.map((g) => [
          combinationFilterKey(g.id),
          multiFilter({
            label: g.name,
            options: g.options,
            getOptionValue: (o) => o.id,
            getOptionLabel: (o) => o.name,
          }),
        ]),
      ),
      // ...then brand.
      brand: multiFilter({
        label: "Brand",
        options: productAttributeOptions.brands,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      // ...then a filter per material attribute dimension present on the products (Finish, Width, …).
      // Beam keys filter options by object identity, so options must be objects — wrap each
      // value string.
      ...Object.fromEntries(
        productAttributeOptions.materialDimensions.map((d) => [
          materialDimensionFilterKey(d.name),
          multiFilter({
            label: d.name,
            options: d.values.map((value) => ({ value })),
            getOptionValue: (o) => o.value,
            getOptionLabel: (o) => o.value,
          }),
        ]),
      ),
    }),
    [combinationGroups, productAttributeOptions],
  );

  // Filter state via `useFilter` (intentionally NOT persisted): columns/filters are generated per option
  // group, so a shared storage key would leak stale selections onto the next group opened. Search state
  // stays on the layout-state hook.
  const { filter, setFilter } = useFilter<VariantsFilter>({ filterDefs });
  const layoutState = useGridTableLayoutState<VariantsFilter>({ search: "client" });

  // Reactive: expansion is derived from cell shape, so columns recompute when cells change, not just slots.
  const columns = useComputed(() => {
    // A collapsed material is only expandable if it actually spans more than one location.
    const expandableMaterialIds = new Set(
      slots
        .groupByObject((s) => s.data.value.materialVariant?.id)
        .filter(([mv, slots]) => !!mv && slots.map((s) => s.data.value.location.id).unique().length > 1)
        .map(([mv]) => mv)
        .compact(),
    );
    return createColumns({
      formState,
      onOpenProductSearch,
      onExpandMaterial,
      onCollapseMaterial,
      onDuplicateProduct,
      expandableMaterialIds,
      placeholderColumns: buildPlaceholderColumns(slots, formState.packageOptions.value),
      targetCombinations,
      tid,
    });
  }, [
    formState,
    slots,
    onOpenProductSearch,
    onExpandMaterial,
    onCollapseMaterial,
    onDuplicateProduct,
    targetCombinations,
    tid,
  ]);

  // Not memoized: must reflect package options being added/removed/edited (read under `observer`).
  // Filters narrow which package options (rows) show; a matching parent keeps its single product child.
  const rows = createRows(
    formState.packageOptions.rows.filter((po) =>
      matchesRowFilter(po, filter, combinationGroups, productAttributeOptions.materialDimensions),
    ),
    !formState.readOnly,
  );

  return (
    <div css={Css.df.fdc.gap2.mw0.fg1.oh.$} {...tid.packageVariantsGrid}>
      <TableActions onlyLeft>
        <SearchBox
          onSearch={layoutState.setSearchString}
          updateQueryString={false}
          placeholder="Search in variants and packages"
        />
        <Filters filterDefs={filterDefs} filter={filter} onChange={setFilter} />
      </TableActions>
      {/* The table's own bounded scroll region (both axes), so the sticky header/columns pin here, not the page. */}
      <div css={Css.fg1.oa.mw0.$}>
        <GridTable
          api={api}
          columns={columns}
          rows={rows}
          filter={layoutState.searchString}
          stickyHeader
          style={tableStyle}
          rowStyles={{
            // Grouped `getTableStyles` draws row dividers via an inset cell box-shadow, not borders.
            packageOption: { cellCss: Css.bsh0.$ },
            products: { rowCss: Css.bb.bcGray200.$, cellCss: Css.bsh0.$ },
          }}
        />
      </div>
    </div>
  );
});

function createRows(packageOptions: PackageOptionRow[], showAddPackageOption: boolean): GridDataRow<Row>[] {
  return [
    { kind: "header", id: "header", data: undefined },
    ...packageOptions.map((packageOption, index) => {
      const id = packageOption.id.value ?? `package-option-${index}`;
      return {
        kind: "packageOption" as const,
        id,
        data: packageOption,
        children: [{ kind: "products" as const, id: `${id}-products`, data: packageOption }],
      };
    }),
    ...(showAddPackageOption ? [{ kind: "addPackageOption" as const, id: "add-package-option", data: undefined }] : []),
  ];
}

/** Whether a package option passes the active row filters (brand, each material dimension, each combination dimension). */
function matchesRowFilter(
  packageOption: PackageOptionRow,
  filter: VariantsFilter,
  combinationGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[],
  materialDimensions: MaterialDimensionOption[],
): boolean {
  // Brand: some assigned product's material is one of the selected brands.
  const brandIds = filter.brand ?? [];
  if (brandIds.nonEmpty) {
    const matches = packageOption.products.value.some((p) => {
      const brandId = p.productMaterial?.data.listing.brand?.id;
      return !!brandId && brandIds.includes(brandId);
    });
    if (!matches) return false;
  }
  // Each material attribute dimension (e.g. Finish, Width): some assigned product has a selected value.
  // Matched by name + `textValue` so the same dimension/value across different items still matches.
  for (const dimension of materialDimensions) {
    const selected = filter[materialDimensionFilterKey(dimension.name)] ?? [];
    if (selected.nonEmpty) {
      const matches = packageOption.products.value.some((p) =>
        (p.productMaterial?.data.materialAttributeValues ?? []).some(
          (av) => av.dimension.name === dimension.name && selected.includes(av.textValue),
        ),
      );
      if (!matches) return false;
    }
  }
  // Each combination dimension (e.g. Scheme, Spec level): the option carries a target combination that
  // includes one of the selected options for that dimension.
  for (const group of combinationGroups) {
    const selected = filter[combinationFilterKey(group.id)] ?? [];
    if (selected.nonEmpty) {
      const matches = packageOption.personalizationTargetCombinations.value.some((combo) =>
        combo.options.some((optionId) => selected.includes(optionId)),
      );
      if (!matches) return false;
    }
  }
  return true;
}

type CreateColumnsProps = {
  formState: ObjectState<FormValue>;
  onOpenProductSearch: (placeholderTliId: string, packageOption: PackageOptionRow, column: PlaceholderColumn) => void;
  onExpandMaterial: (materialVariantId: string) => void;
  onCollapseMaterial: (materialVariantId: string, winningLocationId: string) => void;
  onDuplicateProduct: OnDuplicateProduct;
  /** Materials spanning >1 location, whose collapsed column can offer an "expand" action. */
  expandableMaterialIds: Set<string>;
  placeholderColumns: PlaceholderColumn[];
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
  tid: TestIds;
};

function createColumns(props: CreateColumnsProps): GridColumn<Row>[] {
  const {
    formState,
    onOpenProductSearch,
    onExpandMaterial,
    onCollapseMaterial,
    onDuplicateProduct,
    expandableMaterialIds,
    placeholderColumns,
    targetCombinations,
    tid,
  } = props;
  const slotColumnCount = placeholderColumns.length;

  const slotColumns = placeholderColumns.map((col, columnIndex) => {
    const fragment = col.placeholder.data.value;
    // Grouped pill only when collapsing multiple locations; a single-location material shows its own path.
    const isGrouped = !col.expanded && expandableMaterialIds.has(col.materialVariantId);
    // A grouped column shares one product across all its locations, so "missing" is per package option, not per location.
    const missingPackageOptions = isGrouped
      ? formState.packageOptions.value.filter((po) => !hasProductForColumn(po, col))
      : [];
    return column<Row>({
      id: col.key,
      // `w: 1` (fr) fills width; `mw` is the floor it scrolls past. Don't set table `minWidthPx` — it can zero out fr widths.
      w: 1,
      mw: `${slotMinWidthPx}px`,
      header: () => (
        <div css={Css.marker.df.aic.jcsb.gap1.$}>
          <div css={Css.df.fdc.$}>
            <span css={Css.smSb.$} {...tid.columnName}>
              {fragment.name}
            </span>
            {isGrouped ? (
              <Chip
                compact
                text={`${col.locationPaths.length} grouped slots`}
                title={
                  <>
                    <p>Grouped locations:</p>
                    <ol css={Css.pl3.m0.$}>
                      {col.locationPaths.map((location) => (
                        <li key={location}>{location}</li>
                      ))}
                    </ol>
                    {missingPackageOptions.nonEmpty && (
                      <>
                        <p>Missing products for:</p>
                        <ul css={Css.pl3.m0.$}>
                          {missingPackageOptions.map((po) => (
                            <li key={po.id ?? po.name}>{po.name}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                }
                {...tid.columnLocation}
              />
            ) : (
              <span css={Css.sm.gray600.$} {...tid.columnLocation}>
                {fragment.location.displayLocationPath}
              </span>
            )}
          </div>
          {!formState.readOnly &&
            (col.expanded ? (
              <div css={Css.o0.when(marker, "ancestor", ":hover").o100.$}>
                <IconButton
                  compact
                  icon="link"
                  label="Group to this slot"
                  onClick={() => onCollapseMaterial(col.materialVariantId, col.locationId!)}
                  {...tid.collapseMaterial}
                />
              </div>
            ) : (
              expandableMaterialIds.has(col.materialVariantId) && (
                <div css={Css.relative.$}>
                  <div css={Css.o100.when(marker, "ancestor", ":hover").o0.$}>
                    <Icon icon="link" inc={2} />
                  </div>
                  <div css={Css.absolute.top0.left0.o0.when(marker, "ancestor", ":hover").o100.$}>
                    <IconButton
                      compact
                      icon="unlink"
                      label="Ungroup slots"
                      onClick={() => onExpandMaterial(col.materialVariantId)}
                      {...tid.expandMaterial}
                    />
                  </div>
                </div>
              )
            ))}
        </div>
      ),
      // Package name pins left via CELL-level sticky (column-level would truncate the header/product rows),
      // spanning the first few slots so it stays pinned while the rest scroll.
      packageOption: (packageOption) => {
        if (columnIndex !== 0) return emptyCell;
        const missingColumns = placeholderColumns.filter((c) => !hasProductForColumn(packageOption.value, c));
        return {
          content: (
            <div css={Css.df.aic.gap1.maxwPx(400).w100.$}>
              <div css={Css.fg1.$}>
                <BoundTextField
                  field={packageOption.name}
                  placeholder="Add Package Option Name"
                  labelStyle="hidden"
                  {...tid.packageOptionName}
                />
              </div>
              {missingColumns.nonEmpty && (
                <Tooltip
                  title={
                    <>
                      <p>Missing products for:</p>
                      <ul css={Css.pl2.m1.$}>
                        {missingColumns.map((c) => {
                          const fragment = c.placeholder.data.value;
                          // Expanded columns share a name across locations (e.g. multiple "Tile" columns), so
                          // disambiguate with the location; collapsed columns are already unique by name alone.
                          const label = c.expanded
                            ? `${fragment.name} - ${fragment.location.displayLocationPath}`
                            : fragment.name;
                          return <li key={c.key}>{label}</li>;
                        })}
                      </ul>
                    </>
                  }
                >
                  <Icon icon="circle" color={Palette.Red500} inc={1.5} {...tid.packageOptionMissingProducts} />
                </Tooltip>
              )}
            </div>
          ),
          colspan: Math.min(3, slotColumnCount),
          sticky: "left",
        };
      },
      products: (packageOption) => (
        <ProductCell
          formState={formState}
          onOpenProductSearch={onOpenProductSearch}
          onDuplicateProduct={onDuplicateProduct}
          packageOption={packageOption}
          column={col}
          tid={tid}
        />
      ),
      addPackageOption:
        columnIndex === 0
          ? () => ({
              content: (
                <Button
                  label="Add package option"
                  variant="secondaryBlack"
                  icon="plus"
                  onClick={() =>
                    formState.packageOptions.add({ name: "", products: [], personalizationTargetCombinations: [] })
                  }
                  {...tid.addPackageOption}
                />
              ),
              sticky: "left",
            })
          : emptyCell,
    });
  });

  // Sticky right: combinations + actions, pinned via CELL-level sticky on the parent row only
  // (column-level sticky here truncated the header/product rows).
  const actionsColumn = column<Row>({
    id: "package-option-actions",
    w: `${actionsWidthPx}px`,
    header: emptyCell,
    packageOption: (packageOption, { row, api }) => ({
      content: (
        <PackageOptionActions
          api={api}
          row={row}
          formState={formState}
          packageOption={packageOption}
          targetCombinations={targetCombinations}
          tid={tid}
        />
      ),
      sticky: "right",
    }),
    products: emptyCell,
    addPackageOption: emptyCell,
  });

  return [...slotColumns, actionsColumn];
}

type ProductCellProps = {
  formState: ObjectState<FormValue>;
  onOpenProductSearch: (placeholderTliId: string, packageOption: PackageOptionRow, column: PlaceholderColumn) => void;
  onDuplicateProduct: OnDuplicateProduct;
  packageOption: PackageOptionRow;
  column: PlaceholderColumn;
  tid: TestIds;
};

const productImageSizePx = 96;
const productImageBoxButtonCss = Css.df.fs0.sqPx(productImageSizePx).br8.ba.aic.jcc.cursorPointer.$;

/** A single product cell: image on the left, brand + name on the right (per design). */
const ProductCell = observer(function ProductCell(props: ProductCellProps) {
  const { formState, onOpenProductSearch, onDuplicateProduct, packageOption, column, tid } = props;
  const material = packageOption.products.rows.find((r) => {
    const sameMv = r.placeholderMaterialId.value === column.materialVariantId;
    // A collapsed column matches the single locationless cell; an expanded column matches its location.
    const sameLocation = column.expanded ? r.locationId.value === column.locationId : r.locationId.value == null;
    return sameMv && sameLocation;
  });

  // No cell yet for this slot → an "Add Variant" affordance.
  if (!material) {
    return (
      <div css={Css.mhPx(productImageSizePx).w100.$}>
        <div css={Css.df.gap2.$}>
          {/* Bespoke button to match the square product image box */}
          <button
            type="button"
            aria-label="Add Variant"
            disabled={formState.readOnly}
            css={Css.with(productImageBoxButtonCss).bsDashed.bgBlue50.bcBlue500.$}
            onClick={() => onOpenProductSearch(column.placeholder.id.value, packageOption, column)}
          >
            <Icon icon="plus" color={Palette.Blue500} />
          </button>
        </div>
      </div>
    );
  }

  const mv = material.productMaterial.value?.data;
  const noDuplicateTargetReason = hasEmptyDuplicateTarget(formState, column.materialVariantId)
    ? false
    : "No other empty slots to duplicate this product into.";

  // Explicit "No Product" slot → same square box as product images; click re-opens product search.
  if (!mv) {
    return (
      <div css={Css.marker.mhPx(productImageSizePx).w100.relative.$}>
        <div css={Css.df.gap2.$}>
          <div css={Css.df.fs0.sqPx(productImageSizePx).br8.ba.bcGray200.bgGray50.tac.relative.$}>
            <button
              type="button"
              aria-label="Select Product"
              disabled={formState.readOnly}
              css={Css.w100.h100.df.aic.jcc.cursorPointer.bgTransparent.$}
              onClick={() => onOpenProductSearch(column.placeholder.id.value, packageOption, column)}
            >
              <div {...tid.materialName} css={Css.gray700.sm.$}>
                No Product
              </div>
            </button>
            <div
              css={Css.o0.absolute.top1.right0.z1.when(marker, "ancestor", ":hover").o100.$}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                {...tid.packageOptionNoProductClear}
                bgColor={Palette.White}
                icon="trash"
                disabled={formState.readOnly}
                label="Clear no product"
                onClick={() => packageOption.products.remove(material.value)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...tid.productCard} css={Css.marker.mhPx(productImageSizePx).w100.position("relative").cursorPointer.$}>
      <div css={Css.df.gap2.$}>
        <div css={Css.df.fs0.sqPx(productImageSizePx).br8.ba.bcGray200.oh.relative.$}>
          <img
            css={Css.w100.h100.bgWhite.objectContain.$}
            src={mv.featuredImage?.asset?.previewUrl || PRODUCT_FALLBACK_IMG_URL}
            alt={mv.displayName}
          />
          <div
            css={Css.o0.position("absolute").top1.right0.z1.when(marker, "ancestor", ":hover").o100.$}
            onClick={(e) => e.stopPropagation()}
          >
            <div css={Css.df.fdc.gap1.$}>
              <div css={Css.df.gap1.$}>
                <IconButton
                  {...tid.packageOptionProductDuplicate}
                  bgColor={Palette.White}
                  icon="duplicate"
                  disabled={formState.readOnly || noDuplicateTargetReason}
                  onClick={() => onDuplicateProduct(packageOption, column, material.productMaterial.value!)}
                />

                <IconButton
                  {...tid.packageOptionProductEdit}
                  bgColor={Palette.White}
                  icon="pencil"
                  disabled={formState.readOnly}
                  onClick={() => onOpenProductSearch(column.placeholder.id.value, packageOption, column)}
                />
              </div>
              <div css={Css.asfe.$}>
                <IconButton
                  {...tid.packageOptionProductRemove}
                  bgColor={Palette.White}
                  icon="trash"
                  disabled={formState.readOnly}
                  onClick={() => packageOption.products.remove(material.value)}
                />
              </div>
            </div>
          </div>
        </div>
        <div css={Css.df.fdc.jcsb.aic.fg0.xs.asc.$}>
          <div>
            {mv.listing.brand && <div css={Css.gray600.$}>{mv.listing.brand.name}</div>}
            <div {...tid.materialName} css={Css.lineClamp3.$}>
              {mv.displayName}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

type PackageOptionActionsProps = {
  api: GridTableApi<Row>;
  row: GridRowKind<Row, "packageOption">;
  formState: ObjectState<FormValue>;
  packageOption: PackageOptionRow;
  targetCombinations: EditPersonalizationOptionWizard_OptionCombinationFragment[];
  tid: TestIds;
};

/** The sticky-right combinations selector + actions menu for a package option (its own observer leaf). */
const PackageOptionActions = observer(function PackageOptionActions(props: PackageOptionActionsProps) {
  const { api, row, formState, packageOption, targetCombinations, tid } = props;
  const { openModal } = useModal();
  const productCount = packageOption.products.value.filter((p) => !!p.productMaterial).length;
  //
  const pinRowIds = [row.id, ...row.children.map((c) => c.id)];
  const isPinned = api.isPinnedRow(row.id);
  return (
    <div css={Css.df.aic.gap1.w100.$}>
      {/* `wrap={false}` keeps the combinations chips on one line (the GridTable's context sets wrap=true). */}
      <div css={Css.fg1.mw0.$}>
        <PresentationProvider wrap={false}>
          <MultiSelectField
            label="Option Combinations"
            disabled={targetCombinations.isEmpty || formState.readOnly}
            multiline={false}
            options={targetCombinations}
            placeholder="Select Option Combinations"
            labelStyle="hidden"
            onSelect={(val) => {
              removeDuplicateCombinations(packageOption, formState.packageOptions.rows, val);
              packageOption.personalizationTargetCombinations.set(val.map((v) => ({ options: v.split(",") })));
            }}
            values={packageOption.personalizationTargetCombinations.value.map((c) => c.options.join(","))}
          />
        </PresentationProvider>
      </div>
      <IconButton
        icon="pin"
        color={isPinned ? Palette.Blue600 : undefined} // recreate pinColumn pinned state
        label={isPinned ? "Unpin package option" : "Pin package option"}
        // Ensure child row (always 1) get pinned as well
        onClick={() => pinRowIds.forEach((id) => (isPinned ? api.unpinRow(id) : api.pinRow(id)))}
        {...tid.packageOptionPin}
      />
      <ButtonMenu
        trigger={{ icon: "verticalDots" }}
        placement="right"
        items={[
          {
            label: "Duplicate package option",
            icon: "duplicate",
            disabled: formState.readOnly,
            onClick: () =>
              formState.packageOptions.add({
                name: `COPY of ${packageOption.name.value}`,
                products: packageOption.products.value.map((p) => ({ ...p })),
              }),
          },
          {
            label: "Delete package option",
            icon: "trash",
            destructive: true,
            disabled: formState.readOnly,
            onClick: () =>
              openModal({
                content: (
                  <DeleteConfirmationModal
                    entityType="package option"
                    confirmationMessage={
                      <p css={Css.sm.gray900.$}>
                        Are you sure you want to delete package option{" "}
                        <span css={Css.smSb.$}>{packageOption.name.value}</span>? This package includes{" "}
                        <span css={Css.smSb.$}>{productCount}</span> {productCount === 1 ? "product" : "products"}.
                      </p>
                    }
                    onConfirmDelete={() => formState.packageOptions.remove(packageOption.value)}
                  />
                ),
              }),
          },
        ]}
        {...tid.packageOptionActions}
      />
    </div>
  );
});
